"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { IR_ITEMS, IR_QUESTIONS, type IrItem } from "@/lib/breach/directory/ir-guidance"
import { NeedsCheck, OfficialLinks } from "./directory-ui"

const SECTIONS: { kind: IrItem["kind"]; title: string; intro?: string }[] = [
  { kind: "guidance", title: "What the guidance says" },
  { kind: "list", title: "Official lists and programs", intro: "These are run by standards bodies or agencies. Availability and eligibility vary, so check each one." },
  { kind: "insurer-panel", title: "How insurers handle approved vendors", intro: "If you have cyber insurance, your insurer may approve or require specific firms. See also our insurance directory." },
  { kind: "credential", title: "Credentials you may see", intro: "A credential describes training or testing by that body. It doesn't guarantee a firm's quality." },
  { kind: "checklist", title: "Other selection checklists" },
]

export function IrGuidance() {
  const [copied, setCopied] = useState(false)
  const topics = [...new Set(IR_QUESTIONS.map((q) => q.topic))]
  const all = topics.map((t) => `${t}\n${IR_QUESTIONS.filter((q) => q.topic === t).map((q) => `- ${q.question}`).join("\n")}`).join("\n\n")
  return (
    <div className="grid gap-10">
      {SECTIONS.map((s) => {
        const items = IR_ITEMS.filter((i) => i.kind === s.kind)
        if (!items.length) return null
        return (
          <section key={s.kind} aria-labelledby={`h-${s.kind}`}>
            <h2 id={`h-${s.kind}`} className="text-2xl font-bold">{s.title}</h2>
            {s.intro && <p className="mt-2 text-sm text-muted-foreground">{s.intro}{s.kind === "insurer-panel" && <> <Link href="/insurance" className="underline">Cyber insurance directory</Link>.</>}</p>}
            <ul className="mt-4 grid gap-3">
              {items.map((i) => (
                <li key={i.id} className="rounded-lg border border-border p-4">
                  <h3 className="font-semibold">{i.title}</h3>
                  <p className="text-xs text-muted-foreground">{i.organization}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{i.summary}</p>
                  <OfficialLinks links={i.urls} />
                  <NeedsCheck show={i.needsCheck} />
                </li>
              ))}
            </ul>
          </section>
        )
      })}
      <section aria-labelledby="h-q">
        <h2 id="h-q" className="text-2xl font-bold">Questions to ask before you sign</h2>
        <Button variant="outline" className="mt-3 print:hidden" onClick={async () => { try { await navigator.clipboard.writeText(all); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* ignore */ } }}>
          {copied ? "Copied" : "Copy all questions"}
        </Button>
        {topics.map((t) => (
          <div key={t} className="mt-5">
            <h3 className="font-semibold">{t}</h3>
            <ul className="mt-2 grid gap-2">
              {IR_QUESTIONS.filter((q) => q.topic === t).map((q) => (
                <li key={q.id} className="rounded-md border border-border p-3 text-sm">
                  {q.question}
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {q.sourceUrl ? <a className="underline" href={q.sourceUrl} target="_blank" rel="noopener nofollow">{q.sourceLabel ?? "Source"}</a> : "General practice"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  )
}
